import type { DeploymentAreaDerivedPopulationMetrics } from "@situate/contracts";

export interface ZonePopulation {
  id: string;
  name: string;
  current: number;
  capacity: number;
}

export interface DeploymentMonitoringScope {
  deploymentId: string;
}

/** Sentinel area value representing no area filter (every available area). */
export const ALL_AREAS_FILTER_VALUE = "all-areas";

/** Local UI-only filter state for the Analytics global filter dashboard. */
export interface AnalyticsFilters {
  startDate: Date;
  endDate: Date;
  areaId: string | typeof ALL_AREAS_FILTER_VALUE;
}

/** Deployment area returned by the runtime areas API. */
export interface AreaLocation {
  id: string;
  name: string;
  derivedPopulationMetrics?: DeploymentAreaDerivedPopulationMetrics | null;
  /**
   * Marks the area that should be selected by default on page load.
   * TODO: Update @situate/contracts once backend type is finalized.
   */
  default?: boolean;
}

/**
 * A single live-tracked object's dot on the facility schematic, already mapped
 * into the deployment's floor plan image's own pixel space via
 * `worldToFloorplanPixel`.
 */
export interface LiveTrackPoint {
  /** Area-qualified identifier used to match this point across updates. */
  id: string;
  areaId: string;
  objectId: string;
  /** Original world position in metres, used for distance-based density calculations. */
  worldX: number;
  worldY: number;
  x: number;
  y: number;
  /** Physical person-dot footprint mapped into floorplan coordinates so it scales with the map. */
  footprint: [number, number][];
}

export interface LiveTrackHistoryPoint {
  id: string;
  trackId: string;
  tick: number;
  x: number;
  y: number;
  opacity: number;
  /** Physical trailing-dot footprint mapped into floorplan coordinates so it scales with the map. */
  footprint: [number, number][];
}

export interface LiveTrailSegment {
  id: string;
  start: [number, number];
  end: [number, number];
  opacity: number;
}

export interface LiveHeatmapLayer {
  image: HTMLCanvasElement;
  x: number;
  y: number;
  width: number;
  height: number;
}

export type SensorStatus = "ONLINE" | "OFFLINE" | "MAINTENANCE";

/** A runtime sensor projected into the facility schematic's pixel coordinate space. */
export interface SensorMarkerPoint {
  id: string;
  name: string;
  status: SensorStatus;
  x: number;
  y: number;
}

/**
 * A named observation area (a configured region of interest within a
 * deployment area) to highlight on the facility schematic, already mapped
 * into the schematic's pixel coordinate space via `worldToFloorplanPixel`.
 */
export interface ObservationAreaBoundary {
  id: string;
  name: string;
  /** Ordered vertices tracing the boundary polygon in the schematic's pixel coordinate space; auto-closed. */
  points: { x: number; y: number }[];
}
